/**
 * Change Role dialog (User Management).
 *
 * One role for a single-sided company — the screen every customer and supplier
 * admin has always had. For a company that trades on both portal sides
 * (CR-7255, `is_dual_company`) the same dialog shows a role per side: the
 * customer-side pick moves the single `role` permissions run on, the
 * supplier-side pick is recorded for display and notification routing. Each
 * picker offers only roles of its own side, so a wrong-type role can never be
 * submitted and rejected with a silent 400 (the CR-5294 guard, now per side).
 */
import { useEffect, useMemo, useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Shield } from 'lucide-react';
import type { UserProfile, Role } from '../../lib/api';

/** Sentinel for "no supplier-side role" — shadcn Select cannot hold ''. */
const NO_SUPPLIER_ROLE = 'none';

interface ChangeRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  availableRoles: Role[];
  selectedRoleId: number | null;
  setSelectedRoleId: (id: number | null) => void;
  actionLoading: boolean;
  onChangeRole: () => void | Promise<void>;
  /** CR-7255: save the supplier-side role (null clears it). Dual companies only. */
  onChangeSupplierRole?: (roleId: number | null) => void | Promise<void>;
  /**
   * CR-7255 review: whether the viewer may give, change or remove an admin-type
   * supplier-side role — platform staff or a viewer whose own role is an admin
   * role. Mirrors the backend guard, so the picker never offers a 403.
   */
  canHandleAdminRoles?: boolean;
}

function Label({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={className}>{children}</div>;
}

function RoleSelect({
  roles, value, onChange, placeholder, allowNone,
}: {
  roles: Role[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  allowNone?: boolean;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allowNone && (
          <SelectItem value={NO_SUPPLIER_ROLE}>
            <span className="text-muted-foreground">No supplier-side role</span>
          </SelectItem>
        )}
        {roles.map((role) => (
          <SelectItem key={role.id} value={role.id.toString()}>
            <div className="flex items-center gap-2">
              <span>{role.name}</span>
              {role.is_admin_role && (
                <Badge variant="secondary" className="text-xs">Admin</Badge>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ChangeRoleDialog({
  open,
  onOpenChange,
  selectedUser,
  availableRoles,
  selectedRoleId,
  setSelectedRoleId,
  actionLoading,
  onChangeRole,
  onChangeSupplierRole,
  canHandleAdminRoles = true,
}: ChangeRoleDialogProps) {
  const isDual = Boolean(selectedUser?.is_dual_company);
  // CR-5294: backend rejects assigning a role whose company_type doesn't
  // match the target user's company. Hide incompatible options so the
  // admin can't pick e.g. "Helix Admin" for a customer user and get a
  // silent 400. For a dual company the same rule applies per side.
  const compatibleRoles = useMemo(
    () => (selectedUser
      ? availableRoles.filter((role) => role.company_type === selectedUser.company_type)
      : availableRoles),
    [availableRoles, selectedUser],
  );
  const supplierRoles = useMemo(
    () => availableRoles.filter((role) => role.company_type === 'supplier'
      && (canHandleAdminRoles || !role.is_admin_role)),
    [availableRoles, canHandleAdminRoles],
  );
  const currentSupplierRole = selectedUser?.side_roles?.find((r) => r.side === 'supplier') ?? null;
  // An admin-type supplier-side role is changed only by someone allowed to
  // give one; anyone else sees it but cannot edit it.
  const supplierLocked = Boolean(currentSupplierRole?.is_admin_role) && !canHandleAdminRoles;
  const [supplierRoleId, setSupplierRoleId] = useState<number | null>(null);
  useEffect(() => {
    if (open) setSupplierRoleId(currentSupplierRole?.role ?? null);
  }, [open, currentSupplierRole?.role]);

  const selectedRole = compatibleRoles.find((r) => r.id === selectedRoleId);
  const customerChanged = Boolean(selectedRoleId && selectedRoleId !== selectedUser?.role);
  const supplierChanged = isDual && supplierRoleId !== (currentSupplierRole?.role ?? null);
  const canSave = isDual
    ? Boolean(selectedRoleId) && (customerChanged || supplierChanged)
    : customerChanged;

  const close = () => {
    onOpenChange(false);
    setSelectedRoleId(null);
  };

  const handleSave = async () => {
    if (!isDual) {
      await onChangeRole();
      return;
    }
    // Supplier side first: it does not close the dialog, so a failure there
    // leaves the admin looking at what did not save. The customer-side save
    // closes it on success (existing behaviour).
    if (supplierChanged && onChangeSupplierRole) {
      try {
        await onChangeSupplierRole(supplierRoleId);
      } catch {
        return;
      }
    }
    if (customerChanged) {
      await onChangeRole();
    } else {
      close();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isDual ? 'Change User Roles' : 'Change User Role'}</DialogTitle>
          <DialogDescription>
            {isDual
              ? `${selectedUser?.user.full_name}'s company trades as both a customer and a supplier, so they hold a role on each side.`
              : `Select a new role for ${selectedUser?.user.full_name}`}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">
              {isDual ? 'Current customer-side role' : 'Current User Role'}
            </Label>
            <Badge variant="outline" className="mb-4">{selectedUser?.role_name || 'No role assigned'}</Badge>

            <Label className="text-sm font-medium mb-2 block">
              {isDual ? 'New customer-side role' : 'New User Role'}
            </Label>
            <RoleSelect
              roles={compatibleRoles}
              value={selectedRoleId?.toString() || ''}
              onChange={(value) => setSelectedRoleId(parseInt(value))}
              placeholder="Select a role"
            />
            {selectedRole && (
              <div className="mt-4 p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground">
                  {selectedRole.description}
                </p>
                {/* CR-7076: permissions_summary hidden — it was composed from
                    capability flags nothing enforces (dormant). The plain
                    description is the honest blurb. */}
              </div>
            )}
          </div>

          {isDual && (
            <div className="border-t pt-4" data-testid="supplier-side-role">
              <Label className="text-sm font-medium mb-2 block">Current supplier-side role</Label>
              <Badge variant="outline" className="mb-4">
                {currentSupplierRole?.role_name || 'No supplier-side role'}
              </Badge>
              {supplierLocked ? (
                <p className="text-sm text-muted-foreground" data-testid="supplier-side-role-locked">
                  Only a company admin or platform staff can change an admin role.
                </p>
              ) : (
                <>
                  <Label className="text-sm font-medium mb-2 block">New supplier-side role</Label>
                  <RoleSelect
                    roles={supplierRoles}
                    value={supplierRoleId === null ? NO_SUPPLIER_ROLE : supplierRoleId.toString()}
                    onChange={(value) => setSupplierRoleId(value === NO_SUPPLIER_ROLE ? null : parseInt(value))}
                    placeholder="Select a supplier-side role"
                    allowNone
                  />
                </>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Used for the supplier view and supplier-facing notifications. Permissions
                follow the customer-side role.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={actionLoading || !canSave}>
            <Shield className="h-4 w-4 mr-2" />
            {isDual ? 'Save Roles' : 'Change Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
